/**
 * Pure, dependency-free AppView helpers and types.
 *
 * Split out from `appview.ts` (which pulls in the reactive localStorage store,
 * and therefore the browser globals) so this logic stays unit-testable under
 * the Node test runner.
 */

/**
 * An AppView is the service that aggregates and serves Bluesky-style views
 * (timelines, profiles, notifications, search, …) on top of the AT Protocol
 * network.
 */
export interface AppViewConfig {
	/** Stable identifier. Presets use fixed ids; custom entries use `custom:<url>`. */
	id: string;
	/** Human-readable name shown in the UI. */
	name: string;
	/** Public XRPC base URL used for unauthenticated requests. */
	url: string;
	/**
	 * Service DID (including its `#fragment`) used as the `atproto-proxy` header
	 * value when routing authenticated requests through the user's PDS.
	 */
	did: string;
}

/** Stable id for a custom AppView, derived from (and keyed by) its service URL. */
export const makeCustomAppViewId = (url: string): string => {
	return `custom:${url}`;
};

/** Whether an AppView id refers to a user-defined custom entry. */
export const isCustomAppView = (id: string): boolean => {
	return id.startsWith('custom:');
};

/**
 * Pure insert-or-update of a custom AppView into a list, keyed by id. Returns a
 * new array and never mutates the input.
 */
export const upsertCustomAppView = (customs: AppViewConfig[], entry: AppViewConfig): AppViewConfig[] => {
	const next = customs.slice();
	const existing = next.findIndex((c) => c.id === entry.id);

	if (existing !== -1) {
		next[existing] = entry;
	} else {
		next.push(entry);
	}

	return next;
};
